import { useEffect, useState } from "react";
import {
  ArrowRight,
  Check,
  Mail,
  MapPinned,
  Phone,
  Sparkles,
  Video,
  X,
  ChevronDown,
} from "lucide-react";
import toast from "react-hot-toast";
import useContactForm from "../../services/LandingPage/useContactForm";

export default function ContactDemo({ isOpen, onClose }) {
  const { submitRestaurantBasicInfo, getOrganizationTypes, loading } =
    useContactForm();
const [formData, setFormData] = useState({
  name: "",
  email: "",
  phone: "",
  restaurantName: "",
});
const [organizationTypes, setOrganizationTypes] = useState([]);
const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

useEffect(() => {
  if (!isOpen) return;

  const loadTypes = async () => {
    const res = await getOrganizationTypes();

    if (res.success) {
      setOrganizationTypes(res.data);
    }
  };

  loadTypes();
}, [isOpen]);

  const handleCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setFormData((prev) => ({
          ...prev,
          latitude: position.coords.latitude.toString(),
          longitude: position.coords.longitude.toString(),
        }));
      },
      (error) => {
        switch (error.code) {
          case error.PERMISSION_DENIED:
            toast.error("Location information is unavailable.");
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error("Location information is unavailable.");
            break;
          case error.TIMEOUT:
            toast.error("Location request timed out.");
            break;
          default:
            toast.error("Unable to fetch location.");
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  };

const handleBookDemo = async () => {
  // Required fields
  if (
    !formData.name.trim() ||
    !formData.phone.trim() ||
    !formData.email.trim() ||
    !formData.restaurantName.trim()
  ) {
    toast.error("Please fill all required fields.");
    return;
  }

  // Name validation
  if (!/^[A-Za-z\s]+$/.test(formData.name)) {
    toast.error("Name should contain only letters.");
    return;
  }

  // Phone validation
  if (!/^\d{10}$/.test(formData.phone)) {
   toast.error("Phone number must contain exactly 10 digits.");
    return;
  }

  // Email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!emailRegex.test(formData.email)) {
    toast.error("Please enter a valid email address.");
    return;
  }

  const response = await submitRestaurantBasicInfo(formData);

  console.log("Response:", response);

 if (response.success) {
   toast.success(response.message);

  setFormData({
    name: "",
    email: "",
    phone: "",
    restaurantName: "",
  });

   onClose();
 } else {
   toast.error(response.message);
 }
};

  if (!isOpen) {
    return null;
  }

return (
  <div
    className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
    onMouseDown={onClose}
  >
    <div
      className="relative h-full max-h-[100dvh] w-full max-w-2xl overflow-y-auto rounded-none bg-white shadow-2xl sm:h-auto sm:max-h-[90vh] sm:rounded-[30px]"
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="relative bg-gradient-to-r from-[#01C37D] to-[#00AE70] px-5 py-6 text-white sm:px-8 sm:py-7">
        <button
          onClick={onClose}
          className="absolute right-3 top-3 rounded-full bg-white/20 p-2 transition hover:bg-white/30 sm:right-5 sm:top-5"
        >
          <X size={18} />
        </button>

        <h2 className="text-2xl font-bold sm:text-3xl">Book a Free Demo</h2>

        <p className="mt-2 text-sm leading-6 text-green-50 sm:text-base">
          Fill in your details and our team will get in touch shortly.
        </p>
      </div>

      {/* Form */}
      <div className="space-y-5 p-5 sm:space-y-6 sm:p-8">
        <div className="grid grid-cols-1 gap-x-5 gap-y-6 lg:grid-cols-2">
          {/* Name */}
          <div className="relative">
            <input
              id="name"
              type="text"
              placeholder=" "
              value={formData.name}
              onChange={(e) => {
                const value = e.target.value;

                if (/^[A-Za-z\s]*$/.test(value)) {
                  setFormData({
                    ...formData,
                    name: value,
                  });
                }
              }}
              className="peer h-12 sm:h-14 w-full rounded-xl border border-slate-300 px-4 pt-5 outline-none transition focus:border-[#01C37D] focus:ring-4 focus:ring-green-100"
            />
            <label
              htmlFor="name"
              className="absolute left-4 -top-2 bg-white px-1 text-xs text-slate-500 transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-base peer-focus:-top-2 peer-focus:text-xs peer-focus:text-[#01C37D] peer-[:not(:placeholder-shown)]:-top-2 peer-[:not(:placeholder-shown)]:text-xs"
            >
              Name
              <span className="text-red-500"> *</span>
            </label>
          </div>

          {/* Phone */}
          <div className="relative">
            <input
              id="phone"
              type="tel"
              placeholder=" "
              value={formData.phone}
              maxLength={10}
              inputMode="numeric"
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");

                if (value.length <= 10) {
                  setFormData({
                    ...formData,
                    phone: value,
                  });
                }
              }}
              className="peer h-12 sm:h-14 w-full rounded-xl border border-slate-300 px-4 pt-5 outline-none transition focus:border-[#01C37D] focus:ring-4 focus:ring-green-100"
            />
            <label
              htmlFor="phone"
              className="absolute left-4 -top-2 bg-white px-1 text-xs text-slate-500 transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-base peer-focus:-top-2 peer-focus:text-xs peer-focus:text-[#01C37D] peer-[:not(:placeholder-shown)]:-top-2 peer-[:not(:placeholder-shown)]:text-xs"
            >
              Phone Number
              <span className="text-red-500"> *</span>
            </label>
          </div>

          {/* Email */}
          <div className="relative">
            <input
              id="email"
              type="email"
              placeholder=" "
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              className="peer h-12 sm:h-14 w-full rounded-xl border border-slate-300 px-4 pt-5 outline-none transition focus:border-[#01C37D] focus:ring-4 focus:ring-green-100"
            />
            <label
              htmlFor="email"
              className="absolute left-4 -top-2 bg-white px-1 text-xs text-slate-500 transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-base peer-focus:-top-2 peer-focus:text-xs peer-focus:text-[#01C37D] peer-[:not(:placeholder-shown)]:-top-2 peer-[:not(:placeholder-shown)]:text-xs"
            >
              Email
              <span className="text-red-500"> *</span>
            </label>
          </div>

          {/* Restaurant */}
          <div className="relative">
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowDropdown(!showDropdown)}
                className="flex h-12 sm:h-14 w-full items-center justify-between rounded-xl border border-slate-300 bg-white px-4 text-left outline-none focus:border-[#01C37D] focus:ring-4 focus:ring-green-100"
              >
                <span
                  className={
                    formData.restaurantName
                      ? "text-slate-800"
                      : "text-slate-400"
                  }
                >
                  {formData.restaurantName || "Select Business Type"}
                </span>

                <ChevronDown
                  size={18}
                  className={`transition-transform ${
                    showDropdown ? "rotate-180" : ""
                  }`}
                />
              </button>

              <label className="absolute left-4 -top-2 bg-white px-1 text-xs text-slate-500">
                Restaurant Name <span className="text-red-500">*</span>
              </label>

              {showDropdown && (
                <div className="absolute left-0 z-50 mb-2 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
                  {organizationTypes.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => {
                        setFormData({
                          ...formData,
                          restaurantName: item.restro_type,
                        });
                        setShowDropdown(false);
                      }}
                      className="block w-full px-4 py-3 text-left hover:bg-[#01C37D]/10"
                    >
                      {item.restro_type}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <label
              htmlFor="restaurant"
              className="absolute left-4 -top-2 bg-white px-1 text-xs text-slate-500 transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-base peer-focus:-top-2 peer-focus:text-xs peer-focus:text-[#01C37D] peer-[:not(:placeholder-shown)]:-top-2 peer-[:not(:placeholder-shown)]:text-xs"
            >
              Restaurant Name
              <span className="text-red-500"> *</span>
            </label>
          </div>
        </div>

        <button
          type="button"
          onClick={handleBookDemo}
          disabled={loading}
          className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#01C37D] to-[#00AE70] text-base font-semibold text-white shadow-lg transition hover:scale-[1.01] sm:h-14 sm:text-lg disabled:opacity-60"
        >
          {loading ? (
            <>
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
              Submitting...
            </>
          ) : (
            <>
              Submit Details
              <ArrowRight size={20} />
            </>
          )}
        </button>

        <p className="text-center text-sm text-slate-500">
          Our team will contact you within
          <span className="font-semibold text-[#01C37D]"> 24 hours.</span>
        </p>
      </div>
    </div>
  </div>
);
}


